import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye } from "iconsax-reactjs";
import { toast } from "react-toastify";
import DataTable, {
  Column,
  FilterOption,
} from "../../components/commons/DataTable";
import PostAction from "../../components/partials/post/PostAction";
import PostDetailModal from "./PostDetailModal";
import type {
  Post,
  PostDetail,
  SearchPostParams,
} from "../../services/webServices/post/types";
import { getPostById, getPosts } from "../../services/webServices/post/get";
import { patchPost } from "../../services/webServices/post/patch";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

const PAGE_SIZE = 12;
const FILE_URL = process.env.REACT_APP_File_URL || "";

const filterOptions: FilterOption[] = [
  { value: "published", label: "منتشر شده" },
  { value: "unpublished", label: "در انتظار تایید" },
];

export default function Post() {
  const [data, setData] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState<PostDetail | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchPost = useCallback(async () => {
    if (isFirstLoad) showLoading();
    setIsLoading(true);

    const params: SearchPostParams = {
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    };

    if (filterValue === "published") {
      params.is_published = true;
    }

    if (filterValue === "unpublished") {
      params.is_published = false;
    }

    const response = await getPosts({ params });

    if (Array.isArray(response)) {
      setData(response);

      if (response.length < PAGE_SIZE) {
        setTotalItems((currentPage - 1) * PAGE_SIZE + response.length);
      } else {
        setTotalItems(currentPage * PAGE_SIZE + 1);
      }
    } else {
      setData([]);
      setTotalItems(0);
    }

    setIsLoading(false);
    if (isFirstLoad) hideLoading();
  }, [currentPage, filterValue, hideLoading, isFirstLoad, showLoading]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (value: string) => {
    setFilterValue(value);
    setCurrentPage(1);
  };

  const handleOnRowClick = (row: Post) => {
    window.open(`https://sepehrjou.ir/post/djp-${row.id}`, "_blank");
  };

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedPost(null), 300);
  }, []);

  const handleOpenModal = useCallback(async (post: Post) => {
    setIsModalOpen(true);
    setDetailLoading(true);
    setSelectedPost(null);

    try {
      const detail = await getPostById({ id: post.id });
      setSelectedPost(detail || null);

      if (!detail) {
        toast.error("خطا در دریافت جزئیات پست");
      }
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleUpdatePostStatus = useCallback(
    async (isPublished: boolean) => {
      if (!selectedPost) return;

      if (selectedPost.is_published === isPublished) {
        toast.info("وضعیت همین الان هم همین است");
        return;
      }

      setActionLoading(true);

      try {
        const success = await patchPost({
          id: selectedPost.id,
          body: { is_published: isPublished },
        });

        if (!success) {
          toast.error("خطا در تغییر وضعیت پست");
          return;
        }

        toast.success(
          isPublished ? "پست با موفقیت تایید شد" : "پست با موفقیت رد شد",
        );
        await fetchPost();
        handleCloseModal();
      } finally {
        setActionLoading(false);
      }
    },
    [fetchPost, handleCloseModal, selectedPost],
  );

  const handleRenderAction = (row: Post) => {
    return <PostAction post={row} onActionSuccessed={() => fetchPost()} />;
  };

  const columns: Column<Post>[] = useMemo(
    () => [
      {
        key: "description",
        header: "متن پست",
        align: "right",
        sortable: false,
        width: "40%",
        accessor: (row) => row.description,
      },
      {
        key: "author",
        header: "نویسنده",
        align: "right",
        sortable: true,
        width: "22%",
        accessor: (row) => (
          <div className="flex items-center gap-2">
            <img
              className="h-[20px] w-[20px] rounded-full object-cover"
              src={
                row.user.avatar
                  ? `${FILE_URL}${row.user.avatar}`
                  : "/images/avatar-alt.jpg"
              }
              alt=""
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/images/avatar-alt.jpg";
              }}
            />
            <span>{row.user.full_name || "بدون نام"}</span>
          </div>
        ),
      },
      {
        key: "created_at",
        header: "زمان ایجاد",
        align: "right",
        sortable: true,
        width: "15%",
        accessor: (row) => (
          <span>
            {row.created_at
              ? new Date(row.created_at).toLocaleDateString("fa-IR")
              : "بدون تاریخ"}
          </span>
        ),
      },
      {
        key: "details",
        header: "جزئیات",
        align: "center",
        sortable: false,
        width: "100px",
        accessor: (row) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenModal(row);
            }}
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-blue-600 transition-colors hover:bg-blue-100"
            title="مشاهده جزئیات"
          >
            <Eye size="18" />
          </button>
        ),
      },
    ],
    [handleOpenModal],
  );

  return (
    <div>
      <DataTable
        title="لیست پست‌ها"
        data={data}
        columns={columns}
        loading={isLoading}
        loadingText="در حال بارگذاری پست‌ها..."
        emptyText="پستی یافت نشد"
        filterable={true}
        filterPlaceholder="همه پست‌ها"
        filterOptions={filterOptions}
        filterValue={filterValue}
        onFilterChange={handleFilterChange}
        pagination={true}
        serverSidePagination={true}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}
        onRowClick={handleOnRowClick}
        renderActions={handleRenderAction}
      />

      <PostDetailModal
        open={isModalOpen}
        detailLoading={detailLoading}
        actionLoading={actionLoading}
        post={selectedPost}
        onClose={handleCloseModal}
        onApprove={() => handleUpdatePostStatus(true)}
        onReject={() => handleUpdatePostStatus(false)}
        onPreviewImage={setPreviewImage}
      />

      {previewImage && (
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 left-0 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30"
            >
              ✕
            </button>
            <img
              src={previewImage}
              alt="پیش‌نمایش تصویر"
              className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}
